import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { gql, useQuery as useAppSyncQuery } from "@apollo/client";
import Fuse from "fuse.js";
import React, { useMemo, useContext } from "react";
import moment from "moment";
import { camelCase, mapKeys } from "lodash";
import { AuthContext } from "../context/auth.context";

enum ChannelIds {
  STAFF = "92B392C8-F269-43D1-BCBA-120AB0D41BA8",
  STUDENT = "4E78BF7E-8F85-48C6-AA09-653345C6F2E8",
  COMMUNITY = "fca023b3-2ccf-4fa6-89e9-43acc6d48a11",
}

function useGetEvents() {
  let channelId = "";
  const { userDetails } = useContext(AuthContext);
  if (userDetails?.staff === "true") {
    channelId = ChannelIds.STAFF;
  } else if (userDetails?.studentId !== null) {
    channelId = ChannelIds.STUDENT;
  } else channelId = ChannelIds.COMMUNITY;

  const GET_EVENTS = gql`
  query GetEvents {
    getLUEventsForChannel(channelId: "${channelId}") {
      EventId
      Title
      StartDate
      EndDate
      ShortDescription
      ImageLink
      ImageAltText
      Location {
        LocationName
      }
      Contact {
        ContactId
        EventId
        Name
        Email
        Phone
      }
    }
  }
`;
  return GET_EVENTS;
}

const GET_WW_EVENTS = gql`
  query GetWelcomeWeekEvents {
    getWelcomeWeekEvents(courseId: "") {
      welcomeWeekEvents {
        id
        title
        utcStart
        finishes_at
        long_description
        location {
          latitude
          longitude
          name
          pictureURL
        }
        description
      }
    }
  }
`;

type WelcomeWeekEvent = {
  id: string | null;
  title: string | null;
  utcStart: string | null;
  finishes_at: string | null;
  description: string | null;
  long_description: string | null;
  location: {
    latitude: number | null;
    longitude: number | null;
    name: string | null;
    pictureURL: string | null;
  };
};

export interface Location {
  locationName: string;
}

export interface LUEvent {
  eventId: string;
  location: Location;
  title: string;
  contact: {
    contactId: string | null;
    eventId: string | null;
    name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  startDate: string;
  endDate: string;
  shortDescription: string;
  imageLink?: any;
  imageAltText?: any;
  type: "normal" | "welcomeweek" | "timetable" | "su";
}

type CamelToPascalCase<S extends string> = Capitalize<S>;

type CamelToPascalCaseNested<T> = T extends object
  ? {
      [K in keyof T as CamelToPascalCase<K & string>]: CamelToPascalCaseNested<
        T[K]
      >;
    }
  : T;

type PascalToCamelCase<S extends string> = Uncapitalize<S>;

type PascalToCamelCaseNested<T> = T extends object
  ? {
      [K in keyof T as PascalToCamelCase<K & string>]: PascalToCamelCaseNested<
        T[K]
      >;
    }
  : T;

export interface EventLocation {
  ID: number;
  BeaconMajor: string;
  BeaconMinor: string;
  PlannonID: string;
  Description: string;
  Note: string;
  Longitude: string;
  Latitude: string;
  ImageLocationURL: string;
  Comments?: any;
  LocationReference?: any;
  mapCode: string;
  PositionSupplier: string;
  PositionTShirtSize: string;
  PositionSupplierConferences: number;
}

export const lowerCaseKeys = <T extends object>(
  obj: T
): PascalToCamelCaseNested<T> => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }
  const entries = Object.entries(obj);
  const mappedEntries = entries.map(
    ([k, v]) =>
      [
        `${k.substr(0, 1).toLowerCase()}${k.substr(1)}`,
        lowerCaseKeys(v),
      ] as const
  );
  return Object.fromEntries(mappedEntries) as PascalToCamelCaseNested<T>;
};

export const useEvents = () => {
  const eventsQuerySDL = useGetEvents();
  const eventsQuery = useAppSyncQuery<{
    getLUEventsForChannel: CamelToPascalCaseNested<LUEvent>[];
  }>(eventsQuerySDL);

  const welcomeWeekQuery = useAppSyncQuery<{
    getWelcomeWeekEvents: { welcomeWeekEvents: WelcomeWeekEvent[] };
  }>(GET_WW_EVENTS);

  const events = useMemo(() => {
    if (!eventsQuery.data) return undefined;

    const events = (eventsQuery.data?.getLUEventsForChannel.map((event) => ({
      ...lowerCaseKeys(event),
      type: "normal",
    })) ?? []) as LUEvent[];

    const welcomeWeekEvents =
      welcomeWeekQuery.data?.getWelcomeWeekEvents.welcomeWeekEvents.map(
        (event) =>
          ({
            eventId: event.id?.slice(1),
            location: {
              locationName: event.location?.name ?? "Location Unknown",
            },
            title: event.description,
            contact: null,
            startDate: moment(
              event.utcStart,
              "DD/MM/YYYY HH:mm:ss Z"
            ).toISOString(),
            endDate: event.finishes_at,
            shortDescription: event.long_description,
            imageLink: event.location?.pictureURL,
            imageAltText: "",
            type: "welcomeweek",
          } as LUEvent)
      ) ?? [];

    const allEvents = [...events, ...welcomeWeekEvents]
      .filter((event) => moment(event.startDate).isAfter(moment()))
      .sort((a, b) => a.startDate.localeCompare(b.startDate));

    const uniqueEvents = allEvents.filter(
      (value, index) =>
        index === allEvents.findIndex((t) => t.eventId === value.eventId)
    );

    return uniqueEvents;
  }, [eventsQuery.data, welcomeWeekQuery.data]);

  return {
    data: events,
    isError: !!eventsQuery.error,
    isLoading: eventsQuery.loading || welcomeWeekQuery.loading,
    isSuccess: !!eventsQuery.data,
  } as UseQueryResult<LUEvent[]>;
};

export const useEventsSearch = (query: string) => {
  const events = useEvents();

  const searchResults = useMemo(() => {
    return query.length > 0
      ? events.data?.filter((event) =>
          event.title.toLowerCase().includes(query.toLowerCase())
        ) ?? []
      : [];
  }, [events, query]);

  return { ...events, data: searchResults } as UseQueryResult<LUEvent[]>;
};

export const useEvent = (eventId: string) => {
  const events = useEvents();

  const event = useMemo(() => {
    return events.data?.find((e) => e.eventId === eventId);
  }, [eventId, events]);

  return { ...events, data: event };
};

export const useEventLocation = (event?: LUEvent) => {
  return useQuery(
    ["GetEventLocation", event],
    async () => {
      const response = await fetch(
        `https://itpiapi.lancaster.ac.uk/production/api/AET/v1/Locations/Search?query=${
          event!.location?.locationName
        }`
      );
      if (!response.ok) throw new Error("Error fetching event location");
      const locations: EventLocation[] = await response.json();
      if (locations.length > 0) {
        return locations[0];
      } else {
        throw new Error("Could not find location data by that name");
      }
    },
    { enabled: !!event }
  );
};
